/**
 * Contracts Page - T165
 *
 * List view of all contracts with filtering
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import {
  GlassPanel,
  Button,
  FilterPill,
  LoadingSpinner,
  EmptyState,
  PageHeader,
  Pagination,
  SearchInput,
} from '../components/ui';
import { ContractCard } from '../components/contracts/ContractCard';
import { useContracts, type ContractStatus } from '../hooks/useContracts';

const contractsSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  q: z.string().optional(),
  status: z.enum(['draft', 'active', 'paused', 'completed', 'cancelled']).optional(),
});

type ContractsSearch = z.infer<typeof contractsSearchSchema>;

export const Route = createFileRoute('/contracts')({
  component: ContractsPage,
  validateSearch: contractsSearchSchema,
});

const statusOptions: Array<{ value: ContractStatus | ''; label: string }> = [
  { value: '', label: 'Alle Status' },
  { value: 'draft', label: 'Entwurf' },
  { value: 'active', label: 'Aktiv' },
  { value: 'paused', label: 'Pausiert' },
  { value: 'completed', label: 'Abgeschlossen' },
  { value: 'cancelled', label: 'Storniert' },
];

function ContractsPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const updateSearch = (patch: Partial<ContractsSearch>) => {
    navigate({
      search: (previous: ContractsSearch) => ({ ...previous, ...patch }),
      replace: true,
    });
  };

  const { data, isLoading, error } = useContracts({
    page: search.page,
    limit: 12,
    search: search.q || undefined,
    status: search.status,
    sortBy: 'createdAt',
    sortDirection: 'desc',
  });

  const contracts = data?.data || [];
  const pagination = data?.pagination;

  return (
    <ProtectedRoute>
      <AppShell>
        <PageHeader
          title="Verträge"
          subtitle="Vertragsverwaltung mit wiederkehrender Abrechnung"
          actions={
            <Button onClick={() => navigate({ to: '/contracts/new' })}>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Neuer Vertrag
            </Button>
          }
        />

        {/* Filters — Sprint 006 A3: Suche links, Status-Pills rechts (Kanon) */}
        <GlassPanel className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="flex-1">
              <SearchInput
                placeholder="Suche nach Vertragsnummer oder Titel…"
                value={search.q ?? ''}
                onChange={(q) => updateSearch({ q: q || undefined, page: 1 })}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {statusOptions.map((option) => (
                <FilterPill
                  key={option.value}
                  active={(search.status ?? '') === option.value}
                  onClick={() => updateSearch({ status: option.value || undefined, page: 1 })}
                >
                  {option.label}
                </FilterPill>
              ))}
            </div>
          </div>
        </GlassPanel>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <GlassPanel>
            <p className="text-red-400 text-center py-8">Fehler beim Laden der Verträge</p>
          </GlassPanel>
        ) : contracts.length === 0 ? (
          <EmptyState
            icon={
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                />
              </svg>
            }
            title={search.q || search.status ? 'Keine Verträge gefunden' : 'Noch keine Verträge'}
            description={
              search.q || search.status
                ? 'Versuchen Sie eine andere Suche oder entfernen Sie Filter'
                : 'Erstellen Sie Ihren ersten Vertrag für wiederkehrende Rechnungen'
            }
            action={
              !search.q && !search.status
                ? { label: 'Vertrag erstellen', onClick: () => navigate({ to: '/contracts/new' }) }
                : undefined
            }
          />
        ) : (
          <>
            {/* Grid */}
            <GlassPanel
              variant="subtle"
              padding="sm"
              className="overflow-hidden mb-6"
              data-testid="contracts-list-surface"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {contracts.map((contract) => (
                  <ContractCard key={contract.id} contract={contract} />
                ))}
              </div>
            </GlassPanel>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <Pagination
                page={search.page}
                totalPages={pagination.totalPages}
                onPageChange={(page) => updateSearch({ page })}
              />
            )}
          </>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
